import { useEffect, useState } from 'react'
import { getUiConfig } from '../../../api/identity'
import { ApiError } from '../../../api/problem'

/** `SecurityConfigService.CAPABILITY_UI_COMPONENTS` (IB-289 backend follow-up) — component keys
 * derived from a held `eligibility_overlay:X`/`:A` capability token, additive to the pre-existing
 * Profile-keyed `UiComponentAccess` rows (see that class's doc comment for why a Profile-keyed row
 * alone cannot reflect an independently-assignable PermissionSet like `PS.CartridgeOverlayAuthor`). */
export const COMPONENT_OVERLAY_AUTHOR = 'eligibility.overlay-author'
export const COMPONENT_OVERLAY_APPROVE = 'eligibility.overlay-approve'

export interface OverlayCapabilities {
  /** `null` while the `/me/ui-config` fetch is in flight — callers must not render authoring/
   * approval controls (enabled OR disabled-with-reason) until this resolves (AC1/AC7/AC8: no flash
   * before capability + data resolve). */
  status: 'loading' | 'ready'
  canAuthor: boolean
  canApprove: boolean
  /** Set ONLY when the `/me/ui-config` call itself failed (network/5xx) — distinct from a
   * successful resolve that simply lacks the token. AC7: treated identically to "capability
   * absent" for rendering, but surfaced as its own inline banner so a user can tell "you don't have
   * this" apart from "we couldn't check." */
  error: string | null
}

const LOADING: OverlayCapabilities = { status: 'loading', canAuthor: false, canApprove: false, error: null }

/** AC1/AC3/AC7 — fail-closed capability-gated visibility for the tenant eligibility overlay UI.
 * Never defaults to permissive: any fetch failure resolves to `canAuthor: false, canApprove: false`
 * with `error` set, exactly like a successful-but-absent resolve for rendering purposes. */
export function useOverlayCapabilities(): OverlayCapabilities {
  const [state, setState] = useState<OverlayCapabilities>(LOADING)

  useEffect(() => {
    const controller = new AbortController()
    setState(LOADING)
    getUiConfig(controller.signal)
      .then((config) => {
        if (controller.signal.aborted) return
        setState({
          status: 'ready',
          canAuthor: config.components.includes(COMPONENT_OVERLAY_AUTHOR),
          canApprove: config.components.includes(COMPONENT_OVERLAY_APPROVE),
          error: null,
        })
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setState({
          status: 'ready',
          canAuthor: false,
          canApprove: false,
          error: err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not confirm your access.',
        })
      })
    return () => controller.abort()
  }, [])

  return state
}
